/**
 * Charger Image Cache Service
 * 
 * Provides efficient caching for charger images with:
 * - In-memory cache for fast access
 * - Persistent cache (AsyncStorage) for offline support
 * - TTL (Time To Live) for cache invalidation
 * - Automatic cleanup of stale entries
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { getListOfFiles, downloadFilesFromServer } from '../components/common/utils/file-handler';
import { AttachmentObj } from '../models/file-handler-model';

// Cache configuration
const CACHE_TTL_MS = 1000 * 60 * 30; // 30 minutes
const PERSISTENT_CACHE_KEY = '@charger_image_cache';
const MAX_CACHE_SIZE = 100; // Maximum number of entries in memory

// Types
interface CacheEntry {
  uri: string;
  timestamp: number;
  documentId?: number;
  fileName?: string;
}

interface CacheStore {
  [chargerId: string]: CacheEntry;
}

// In-memory cache
let memoryCache: CacheStore = {};
let cacheInitialized = false;

/**
 * Initialize the cache by loading from persistent storage
 */
export const initializeCache = async (): Promise<void> => {
  if (cacheInitialized) return;
  
  try {
    const stored = await AsyncStorage.getItem(PERSISTENT_CACHE_KEY);
    if (stored) {
      const parsedCache: CacheStore = JSON.parse(stored);
      const now = Date.now();
      
      // Filter out expired entries
      memoryCache = Object.entries(parsedCache).reduce((acc, [key, entry]) => {
        if (now - entry.timestamp < CACHE_TTL_MS) {
          acc[key] = entry;
        }
        return acc;
      }, {} as CacheStore);
      
      // Loaded cached charger images
    }
    cacheInitialized = true;
  } catch (error) {
    console.error('Error initializing image cache:', error);
    memoryCache = {};
    cacheInitialized = true;
  }
};

/**
 * Save the current cache to persistent storage
 */
const persistCache = async (): Promise<void> => {
  try {
    // Limit cache size
    const entries = Object.entries(memoryCache);
    if (entries.length > MAX_CACHE_SIZE) {
      // Keep only the most recent entries
      const sorted = entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
      memoryCache = Object.fromEntries(sorted.slice(0, MAX_CACHE_SIZE));
    }
    
    await AsyncStorage.setItem(PERSISTENT_CACHE_KEY, JSON.stringify(memoryCache));
  } catch (error) {
    console.error('Error persisting image cache:', error);
  }
};

/**
 * Get cached image URI for a charger
 */
export const getCachedImageUri = async (chargerId: string): Promise<string | null> => {
  await initializeCache();
  
  const entry = memoryCache[chargerId];
  if (!entry) return null;
  
  // Check if entry is expired
  const now = Date.now();
  if (now - entry.timestamp > CACHE_TTL_MS) {
    // Cache expired, remove it
    delete memoryCache[chargerId];
    await persistCache();
    return null;
  }
  
  return entry.uri;
};

/**
 * Set cached image URI for a charger
 */
export const setCachedImageUri = async (
  chargerId: string, 
  uri: string,
  documentId?: number,
  fileName?: string
): Promise<void> => {
  await initializeCache();
  
  memoryCache[chargerId] = {
    uri,
    timestamp: Date.now(),
    documentId,
    fileName,
  };
  
  // Persist in background (don't wait)
  persistCache().catch(console.error);
};

/**
 * Invalidate cache entry for a specific charger
 */
export const invalidateCacheEntry = async (chargerId: string): Promise<void> => {
  await initializeCache();
  
  if (memoryCache[chargerId]) {
    delete memoryCache[chargerId];
    await persistCache();
  }
};

/**
 * Clear all cached images
 */
export const clearImageCache = async (): Promise<void> => {
  memoryCache = {};
  try {
    await AsyncStorage.removeItem(PERSISTENT_CACHE_KEY);
  } catch (error) {
    console.error('Error clearing image cache:', error);
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = async (): Promise<{
  entries: number;
  oldestEntry: number | null;
  newestEntry: number | null;
}> => {
  await initializeCache();
  
  const entries = Object.values(memoryCache);
  const timestamps = entries.map(e => e.timestamp);
  
  return {
    entries: entries.length,
    oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : null,
    newestEntry: timestamps.length > 0 ? Math.max(...timestamps) : null,
  };
};

/**
 * Load and cache a charger's thumbnail image
 * 
 * This function:
 * 1. Checks the cache first
 * 2. If not cached, fetches file list for the charger
 * 3. Downloads the thumbnail version
 * 4. Caches the result
 * 5. Returns the URI (or null if no image)
 */
export const loadChargerThumbnail = async (
  chargerId: string,
  version: 'thumbnail' | 'medium' | 'original' = 'thumbnail',
  forceRefresh: boolean = false
): Promise<string | null> => {
  try {
    // Check cache first (unless force refresh)
    // Note: Cache key should include version to avoid conflicts
    const cacheKey = version === 'thumbnail' ? chargerId : `${chargerId}_${version}`;
    if (!forceRefresh) {
      const cachedUri = await getCachedImageUri(cacheKey);
      if (cachedUri) {
        // Using cached image
        return cachedUri;
      }
    }
    
    // Fetch file list for this charger
    const files = await getListOfFiles(chargerId);
    
    if (!files || files.length === 0) {
      // No files found for this charger
      return null;
    }
    
    // Find the first image file
    const imageFile = files.find((file: AttachmentObj) => {
      const isImage = file.attachment?.type?.startsWith('image/') || 
                     file.documentname?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
      return isImage;
    });
    
    if (!imageFile) {
      // No image files found for this charger
      return null;
    }
    
    // Download the image
    
    // Download with specified version
    if (Platform.OS === 'web') {
      const { downloadFileBinaryWeb } = require('../services/file-handler-service');
      const JSZip = require('jszip');
      
      const body = {
        action: 100,
        docid: imageFile.documentid,
        documentname: imageFile.documentname,
        version: version,
      };
      
      const response = await downloadFileBinaryWeb('filehandler/download', body);
      
      if (response.error) {
        console.error('Failed to download image:', response.error);
        return null;
      }
      
      // Decompress
      const zip = new JSZip();
      const loadedZip = await zip.loadAsync(response.blob);
      const firstFile = Object.keys(loadedZip.files)[0];
      const unzippedBlob = await loadedZip.files[firstFile].async('blob');
      
      // Create blob URL
      const blobUrl = URL.createObjectURL(unzippedBlob);
      
      // Cache it
      await setCachedImageUri(cacheKey, blobUrl, imageFile.documentid, imageFile.documentname);
      
      return blobUrl;
    } else {
      // Native
      const downloadedUri = await downloadFilesFromServer(imageFile, undefined, version);
      
      if (downloadedUri) {
        // Cache it
        await setCachedImageUri(cacheKey, downloadedUri, imageFile.documentid, imageFile.documentname);
        
        return downloadedUri;
      }
      
      return null;
    }
  } catch (error) {
    console.error(`Error loading thumbnail for charger ${chargerId}:`, error);
    return null;
  }
};

/**
 * Prefetch thumbnails for multiple chargers (for list views)
 * This loads images in the background to improve perceived performance
 */
export const prefetchChargerThumbnails = async (
  chargerIds: string[],
  maxConcurrent: number = 3
): Promise<void> => {
  // Prefetching thumbnails in background
  
  // Process in batches
  for (let i = 0; i < chargerIds.length; i += maxConcurrent) {
    const batch = chargerIds.slice(i, i + maxConcurrent);
    
    await Promise.allSettled(
      batch.map(chargerId => loadChargerThumbnail(chargerId))
    );
  }
  
  // Prefetch complete
};

